import type {UseMutationOptions} from '@tanstack/react-query';
import {postAppSetting} from '../api/app/postAppSetting';
import {PagewebError} from '../errors/PagewebError.class';
import {
  AppSettingMutation,
  type AppSettingMutationVariables,
  ResultStatus,
} from '../graphql/generated/types';

/**
 * App 도메인 Mutation Adapter
 */
export const appMutationAdapter = {
  /**
   * 앱 설정
   *
   * ⚠️ Resolver 로직 완전 복제:
   * - try-catch로 에러를 잡아서 항상 성공 응답 반환
   * - 성공: {status: ResultStatus.Success}
   * - 실패: {status: ResultStatus.Fail}
   * - React Query의 error state는 사용하지 않음 (항상 성공)
   * - 실제 성공/실패는 data.status로 판단해야 함
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(appMutationAdapter.appSetting());
   * mutation.mutate({input: {tastesHome: true}});
   * // 성공 여부는 mutation.data?.status === ResultStatus.Success로 확인
   * ```
   */
  appSetting: (): UseMutationOptions<
    AppSettingMutation,
    PagewebError,
    AppSettingMutationVariables
  > => ({
    mutationFn: async (variables: AppSettingMutationVariables) => {
      try {
        await postAppSetting(variables.input);
        return {
          appSetting: {
            status: ResultStatus.Success,
          },
        };
      } catch (_e) {
        // Resolver와 동일하게 에러를 throw하지 않고 Fail 상태 반환
        return {
          appSetting: {
            status: ResultStatus.Fail,
          },
        };
      }
    },
  }),
} as const;
